import { ethers } from 'ethers';

/**
 * Format WEI amount to human-readable ETCPOWv2 tokens
 */
export const formatToken = (weiAmount: string | bigint | number, decimals: number = 2): string => {
  try {
    // Handle zero or empty values
    if (!weiAmount || weiAmount === '0' || weiAmount === 0) {
      return '0';
    }
    
    // If it's already a formatted string (like "431.9K"), return it as-is
    if (typeof weiAmount === 'string' && (weiAmount.includes('K') || weiAmount.includes('M') || weiAmount.includes('B'))) {
      return weiAmount;
    }
    
    // Check if it's already a decimal number (not wei)
    if (typeof weiAmount === 'string' && weiAmount.includes('.')) {
      const num = parseFloat(weiAmount);
      if (num === 0) return '0';
      if (num < 0.01) return num.toFixed(4); // Show more precision for small amounts
      if (num < 1) return num.toFixed(decimals);
      if (num < 1000) return num.toFixed(1);
      if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
      if (num < 1000000000) return `${(num / 1000000).toFixed(1)}M`;
      return `${(num / 1000000000).toFixed(1)}B`;
    }
    
    // If it's a number (not string), check if it's already in decimal form
    if (typeof weiAmount === 'number' && weiAmount < 1000000) {
      // Likely already in decimal form, not wei
      const num = weiAmount;
      if (num === 0) return '0';
      if (num < 0.01) return num.toFixed(4); // Show more precision for small amounts
      if (num < 1) return num.toFixed(decimals);
      if (num < 1000) return num.toFixed(1);
      if (num < 1000000000) return `${(num / 1000).toFixed(1)}K`;
      if (num < 1000000000) return `${(num / 1000000).toFixed(1)}M`;
      return `${(num / 1000000000).toFixed(1)}B`;
    }
    
    // Use ethers.formatEther for wei conversion
    const tokenAmount = ethers.formatEther(weiAmount.toString());
    const num = parseFloat(tokenAmount);
    
    if (num === 0) return '0';
    if (num < 0.01) return num.toFixed(4); // Show more precision for small amounts
    if (num < 1) return num.toFixed(decimals);
    if (num < 1000) return num.toFixed(1);
    if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
    if (num < 1000000000) return `${(num / 1000000).toFixed(1)}M`;
    return `${(num / 1000000000).toFixed(1)}B`;
  } catch (error) {
    console.error('Error formatting token amount:', error);
    return '0';
  }
};

/**
 * Format small amounts with high precision (for pending rewards)
 */
export const formatSmallAmount = (amount: string | bigint | number, decimals: number = 6): string => {
  try {
    // Handle zero or empty values
    if (!amount || amount === '0' || amount === 0) {
      return '0';
    }
    
    // If it's already a decimal number (not wei), just format it
    if (typeof amount === 'number' || (typeof amount === 'string' && amount.includes('.'))) {
      const num = typeof amount === 'number' ? amount : parseFloat(amount);
      if (num === 0) return '0';
      return num.toFixed(decimals);
    }
    
    // If it's wei, convert to decimal first
    const tokenAmount = ethers.formatEther(amount.toString());
    const num = parseFloat(tokenAmount);
    
    if (num === 0) return '0';
    return num.toFixed(decimals);
  } catch (error) {
    console.error('Error formatting small amount:', error);
    return '0';
  }
};

/**
 * Format WEI amount to full ETCPOWv2 tokens (no abbreviation)
 */
export const formatFullToken = (weiAmount: string | bigint | number, decimals: number = 4): string => {
  try {
    const amount = ethers.formatEther(weiAmount.toString());
    const num = parseFloat(amount);
    
    if (num === 0) return '0';
    return num.toFixed(decimals);
  } catch (error) {
    console.error('Error formatting token amount:', error);
    return '0';
  }
};

/**
 * Convert human-readable amount to WEI
 */
export const toWei = (amount: string | number): bigint => {
  try {
    return ethers.parseEther(amount.toString());
  } catch (error) {
    console.error('Error converting to WEI:', error);
    return BigInt(0);
  }
};

/**
 * Format gas price to Gwei
 */
export const formatGasPrice = (gasPrice: string | bigint | number): string => {
  try {
    const gwei = ethers.formatUnits(gasPrice.toString(), 'gwei');
    return `${parseFloat(gwei).toFixed(2)} Gwei`;
  } catch (error) {
    console.error('Error formatting gas price:', error);
    return '0 Gwei';
  }
};

/**
 * Format time duration
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

/**
 * Format percentage
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Format large numbers with commas
 */
export const formatNumber = (num: number | string): string => {
  const n = typeof num === 'string' ? parseFloat(num) : num;
  return n.toLocaleString('en-US');
};

/**
 * Format hash power with appropriate units
 */
export const formatHashPower = (hashPower: number): string => {
  if (hashPower < 1000) return `${hashPower} H/s`;
  if (hashPower < 1000000) return `${(hashPower / 1000).toFixed(1)} KH/s`;
  if (hashPower < 1000000000) return `${(hashPower / 1000000).toFixed(1)} MH/s`;
  return `${(hashPower / 1000000000).toFixed(1)} GH/s`;
};

/**
 * Format bandwidth with appropriate units
 */
export const formatBandwidth = (bandwidth: number): string => {
  if (bandwidth < 1000) return `${bandwidth} KB/s`;
  if (bandwidth < 1000000) return `${(bandwidth / 1000).toFixed(1)} MB/s`;
  return `${(bandwidth / 1000000).toFixed(1)} GB/s`;
};

/**
 * Format uptime as percentage
 */
export const formatUptime = (uptime: number): string => {
  return `${(uptime / 100).toFixed(1)}%`;
};

/**
 * Get tier name from tier number
 */
export const getTierName = (tier: number): string => {
  const tiers = {
    1: 'Common',
    2: 'Uncommon',
    3: 'Rare',
    4: 'Epic',
    5: 'Legendary'
  };
  return tiers[tier as keyof typeof tiers] || 'Unknown';
};

/**
 * Get tier color
 */
export const getTierColor = (tier: number): string => {
  const colors = {
    1: 'text-gray-400', // Common
    2: 'text-green-400', // Uncommon
    3: 'text-cyan-400',  // Rare
    4: 'text-amber-400', // Epic
    5: 'text-yellow-300',  // Legendary
  };
  return colors[tier as keyof typeof colors] || 'text-gray-400';
};

/**
 * Get rarity color
 */
export const getRarityColor = (rarity: number): string => {
  const colors = {
    1: 'bg-gray-600', // Common
    2: 'bg-green-600', // Uncommon
    3: 'bg-cyan-600',  // Rare
    4: 'bg-amber-600', // Epic
    5: 'bg-yellow-500',  // Legendary
  };
  return colors[rarity as keyof typeof colors] || 'bg-gray-600';
};

/**
 * Get rarity name
 */
export const getRarityName = (rarity: number): string => {
  const names = {
    1: 'Common',
    2: 'Uncommon',
    3: 'Rare',
    4: 'Epic',
    5: 'Legendary',
  };
  return names[rarity as keyof typeof names] || 'Unknown';
};

/**
 * Get stat icon
 */
export const getStatIcon = (stat: string): string => {
  const icons = {
    'hashPower': '⚡',
    'bandwidth': '📡',
    'uptime': '⏰',
  };
  return icons[stat as keyof typeof icons] || '🔧';
};

/**
 * Get stat name
 */
export const getStatName = (stat: string): string => {
  const names = {
    'hashPower': 'Hash Power',
    'bandwidth': 'Bandwidth',
    'uptime': 'Uptime',
  };
  return names[stat as keyof typeof names] || stat;
};

/**
 * Format transaction status
 */
export const formatTxStatus = (status: string): string => {
  const statuses = {
    'pending': '⏳ Pending',
    'confirmed': '✅ Confirmed',
    'failed': '❌ Failed',
    'reverted': '🔄 Reverted',
  };
  return statuses[status as keyof typeof statuses] || status;
};

/**
 * Truncate address for display
 */
export const truncateAddress = (address: string, length: number = 6): string => {
  if (!address) return '';
  if (address.length <= length * 2) return address;
  return `${address.slice(0, length)}...${address.slice(-length)}`;
};

/**
 * Format date
 */
export const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString();
};

/**
 * Format time ago
 */
export const formatTimeAgo = (timestamp: number): string => {
  const now = Date.now() / 1000;
  const diff = now - timestamp;
  
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
  return formatDate(timestamp);
};
